import { useState, type FormEvent, useEffect } from "react";
import { Link, useNavigate } from "react-router";
import { Alert, Button, TextInput, useToast } from "@/ui";
import { SessionExpiredNotice } from "@/auth/SessionExpiredNotice";
import { AuthCard, AuthLayout } from "@/layout/AuthLayout";
import { useAuth } from "@/auth/AuthProvider";
import { homeFor } from "@/auth/RequireAuth";
import { useTenant } from "@/tenant/TenantProvider";
import { ApiClientError } from "@/lib/api";
import { InstallAppButton } from "@/components/InstallAppButton";

/** Super admin sign-in — no org slug (D-03, D-05, D-06). */
export function PlatformLogin() {
  const { login, twoFaPending } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const { error: tenantError } = useTenant();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [fields, setFields] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);

  // Navigate to 2FA page when login triggers it
  useEffect(() => {
    if (twoFaPending) {
      navigate("/login/twofa", { replace: true });
    }
  }, [twoFaPending, navigate]);

  async function handleSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    setFields({});
    setSubmitting(true);
    try {
      const user = await login(null, email, password);
      // Only navigate if 2FA is not pending (useEffect handles that)
      if (!twoFaPending) {
        navigate(user.mustChangePassword ? "/set-password" : homeFor(user), { replace: true });
      }
    } catch (err) {
      if (err instanceof ApiClientError) {
        setFields(err.fields ?? {});
        toast.error(err.message);
      } else {
        toast.error("Something went wrong. Please try again.");
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <AuthLayout
      accent="one platform."
      blurb="Create organizations, hand each one its own branded rewards portal, and keep every tenant's data completely separate."
      headline="Many businesses,"
      points={[
        "Spin up a new organization in seconds",
        "Each one gets its own URL and its own branding",
        "Full audit trail on every action",
      ]}
    >
      <AuthCard subtitle="Platform sign in" title="Welcome back">
        <SessionExpiredNotice />
        <form className="flex flex-col gap-4" noValidate onSubmit={handleSubmit}>
          <TextInput
            autoComplete="email"
            autoFocus
            error={fields.email}
            label="Email"
            onChange={(e) => setEmail(e.target.value)}
            required
            type="email"
            value={email}
          />
          <TextInput
            autoComplete="current-password"
            error={fields.password}
            label="Password"
            onChange={(e) => setPassword(e.target.value)}
            required
            type="password"
            value={password}
          />
          <Link className="-mt-2 self-end text-sm font-medium text-brand-ink underline" to="/forgot-password">
            Forgot password?
          </Link>
          {tenantError ? <Alert kind="danger">{tenantError}</Alert> : null}
          <Button block loading={submitting} type="submit">
            Sign in
          </Button>
          <InstallAppButton label="Install app" />
        </form>
      </AuthCard>
    </AuthLayout>
  );
}
